import {
  forwardRef,
  ComponentPropsWithoutRef,
  useId,
  useRef,
  ChangeEvent,
  KeyboardEvent,
  useState,
  useEffect,
} from "react";
import cx from "classnames";

import textfieldStyles from "../../TextField/TextField.module.scss";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Search from "@servicetitan/hammer-icon/mdi/round/search.svg";
import styles from "../SearchField.module.scss";
import { useLayoutPropsUtil } from "../../../internal/hooks";
import { SrOnly } from "../../SrOnly";
import { Icon } from "../../Icon";
import { useTrackingId, useMergeRefs } from "../../../hooks";
import { DataTrackingId, LayoutUtilProps, Size } from "../../../types";

/**
 * Props for the SearchField component
 * @extends Omit<ComponentPropsWithoutRef<"input">, "size" | "prefix" | "required" | "children">
 * @extends LayoutUtilProps
 */
export type SearchFieldProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "size" | "prefix" | "required" | "children"
> &
  LayoutUtilProps & {
    /**
     * Size of the search field
     * @default "medium"
     */
    size?: Extract<Size, "xsmall" | "small" | "medium" | "large">;
    /**
     * Whether the search field is disabled
     * @default false
     */
    disabled?: boolean;
    /**
     * Callback when the clear button is clicked or activated via keyboard (Enter/Space)
     */
    onClear?: () => void;
  } & DataTrackingId;

/**
 * SearchField component for text input with search functionality.
 *
 * Features:
 * - Search icon prefix for visual clarity
 * - Clear button that appears when input has value
 * - Arrow key navigation between input and clear button (Right arrow to clear button, Left arrow back to input)
 * - Clear button activated with Enter/Space keys
 * - Focus returns to input after clearing
 * - Multiple size variants (xsmall, small, medium, large)
 * - Controlled and uncontrolled modes
 * - Disabled and read-only states
 * - Accessibility support with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Automatic value detection for clear button visibility
 * - Search-specific input type and autocomplete settings
 * - Screen reader support with placeholder announcements
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <SearchField
 *   placeholder="Search users..."
 *   onChange={(e) => setSearchTerm(e.target.value)}
 *   onClear={() => setSearchTerm("")}
 * />
 *
 * @example
 * <SearchField
 *   size="large"
 *   value={searchValue}
 *   onChange={(e) => setSearchValue(e.target.value)}
 *   disabled={isLoading}
 * />
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      size,
      onChange,
      onClear,
      id: idProp,
      disabled,
      style,
      value,
      placeholder = "Search...",
      autoComplete = "off",
      ...rest
    } = componentProps;

    const [hasValue, setHasValue] = useState(
      rest.defaultValue || value ? true : false,
    );
    const placeholderUid = useId();

    const inputRef = useRef<HTMLInputElement>(null);

    const styleCombined = { ...style, ...layoutStyles };

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value) {
        setHasValue(true);
      } else {
        setHasValue(false);
      }
      onChange?.(e);
    };

    useEffect(() => {
      if (typeof value === "undefined") return;
      if (value === "" || !value) {
        setHasValue(false);
      } else {
        setHasValue(true);
      }
    }, [value]);

    const handleClear = () => {
      if (!inputRef.current) return;
      if (disabled || rest.readOnly) return;
      onClear?.();
      if (value) {
        inputRef.current.focus();
        return;
      }
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value",
      )?.set;
      nativeInputValueSetter?.call(inputRef.current, "");
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
      setHasValue(false);
      inputRef.current.focus();
    };

    const handleClearKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
      if (e.code === "Enter" || e.code === "Space") {
        e.preventDefault();
        handleClear();
      } else if (e.code === "ArrowLeft") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.code === "ArrowRight" && hasValue) {
        const input = e.currentTarget;
        if (input.selectionStart === input.value.length) {
          e.preventDefault();
          const clearButton = input.parentElement?.querySelector(
            '[role="button"]',
          ) as HTMLElement;
          clearButton?.focus();
        }
      }
    };

    const data = {
      value: props.value,
      defaultValue: props.defaultValue,
      disabled: props.disabled,
      readOnly: props.readOnly,
      size: props.size,
    };

    const trackingId = useTrackingId({
      name: "SearchField",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    return (
      <div
        className={cx(
          textfieldStyles["textfield"],
          styles["searchfield"],
          className,
        )}
        data-anv="SearchField"
        style={styleCombined}
      >
        <div
          className={cx(
            textfieldStyles["input-wrapper"],
            styles["input-wrapper"],
            {
              [textfieldStyles["xsmall"]]: size === "xsmall",
              [textfieldStyles["small"]]: size === "small",
              [textfieldStyles["large"]]: size === "large",
            },
          )}
          role="presentation"
          onClick={() => inputRef.current?.focus()}
        >
          <div aria-hidden className={textfieldStyles["prefix"]}>
            <Icon aria-label="search" svg={Search} />
          </div>
          <input
            className={cx(textfieldStyles["input"], styles["input"], {
              [styles["input--has-value"]]: hasValue,
            })}
            ref={useMergeRefs([ref, inputRef])}
            onChange={handleOnChange}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            value={value}
            type="search"
            autoComplete={autoComplete}
            disabled={disabled}
            aria-labelledby={placeholderUid}
            data-tracking-id={trackingId}
            {...rest}
          />
          <Icon
            aria-label="Clear search"
            svg={Close}
            role="button"
            onClick={handleClear}
            onKeyDown={handleClearKeyDown}
            tabIndex={-1}
            className={styles["clear"]}
            aria-disabled={disabled}
            style={{
              visibility: hasValue ? "visible" : "hidden",
            }}
          />
        </div>
        <SrOnly id={placeholderUid}>{placeholder}</SrOnly>
      </div>
    );
  },
);
